import { useState } from "react";
import PanelCollapse from "@shared/v1/Panel/PanelCollapse";
import { ChevronRightSolidV6, ChevronUpSolidV6 } from "@shared/v2/Icomoon/Icons";
import stageShape from "../model/stage";
import { formatStageDate } from "./helpers";
import MilestoneProgressBar from "./MilestoneProgressBar";
import MilestoneList from "./MilestoneList";

const YELLOW_TITLE_STATUS = "satisfying_requirements";

const titleStatusClassName = (value) =>
  value === YELLOW_TITLE_STATUS
    ? "tw-font-semibold tw-text-semantic-yellow-100"
    : "tw-font-semibold tw-text-gray-75";

const InProgressStage = ({ stage }) => {
  const [expanded, setExpanded] = useState(false);

  const {
    primaryMilestone,
    milestones,
    earnestMoneyStatus,
    titleStatus,
    estimatedSigning,
    cdaStatus,
    fundingStatus,
    recordingStatus,
    escrowOfficer,
    lastUpdated,
  } = stage;

  if (!primaryMilestone) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-[12px]">
      <div className="tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-neutral-gray-10 tw-p-[8px]">
        <div className="tw-flex tw-justify-between tw-items-start tw-gap-[8px]">
          <span className="tw-text-14 tw-font-semibold tw-leading-20px tw-text-gray-75">
            {primaryMilestone?.title}
          </span>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="tw-flex tw-items-center tw-gap-[4px] tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-text-12px tw-leading-[16px] tw-font-normal tw-text-gray-75 tw-whitespace-nowrap tw-shrink-0"
          >
            {expanded ? "Collapse" : "View progress"}
            {expanded ? (
              <ChevronUpSolidV6 size="custom" className="tw-w-[8px] tw-h-[8px]" />
            ) : (
              <ChevronRightSolidV6 size="custom" className="tw-w-[8px] tw-h-[8px]" />
            )}
          </button>
        </div>
        {!expanded && <MilestoneProgressBar milestones={milestones} />}
        <PanelCollapse collapsed={!expanded}>
          <MilestoneList milestones={milestones} />
        </PanelCollapse>
      </div>

      {earnestMoneyStatus && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">Earnest Money:</span>
          <span className="tw-font-semibold tw-text-gray-75">{earnestMoneyStatus.title}</span>
        </div>
      )}

      {titleStatus && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">Title Status:</span>
          <span className={titleStatusClassName(titleStatus.value)}>{titleStatus.title}</span>
        </div>
      )}

      {estimatedSigning && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">Estimated Buyer Signing:</span>
          <span className="tw-font-semibold tw-text-gray-75">{estimatedSigning.title}</span>
        </div>
      )}

      {cdaStatus && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">CDA Status:</span>
          <span className="tw-font-semibold tw-text-gray-75">{cdaStatus.title}</span>
        </div>
      )}

      {fundingStatus && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">Funding Status:</span>
          <span className="tw-font-semibold tw-text-gray-75">{fundingStatus.title}</span>
        </div>
      )}

      {recordingStatus && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">Recording:</span>
          <span className="tw-font-semibold tw-text-gray-75">{recordingStatus.title}</span>
        </div>
      )}

      {escrowOfficer?.name && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-leading-[16px]">
          <span className="tw-font-normal tw-text-gray-75">Escrow Officer:</span>
          <span className="tw-font-semibold tw-text-gray-75">{escrowOfficer.name}</span>
        </div>
      )}

      {lastUpdated && (
        <div className="tw-flex tw-justify-between tw-text-12px tw-font-normal tw-italic tw-leading-[16px] tw-text-gray-50">
          <span>Last Updated:</span>
          <span>{formatStageDate(lastUpdated)}</span>
        </div>
      )}
    </div>
  );
};

InProgressStage.propTypes = {
  stage: stageShape.isRequired,
};

export default InProgressStage;
